function renderGenRestoreContentServerActionTemplate({
  join,
  ctxCacheInvalidationFn,
  ctxCamelPlural,
  ctxSingular,
  kebabSingular,
  snapshotImport,
  lifecycleHookImport,
  ctxPlural,
  restoreContext,
  beforeHook,
  ctxTableVar,
  currentSnapshot,
  restoreM2MContent,
  afterHookAfterRestore,
  ctxCamelSingular,
  ctxSingularText
}: {
  join: string
  ctxCacheInvalidationFn: string
  ctxCamelPlural: string
  ctxSingular: string
  kebabSingular: string
  snapshotImport: string
  lifecycleHookImport: string
  ctxPlural: string
  restoreContext: string
  beforeHook: string
  ctxTableVar: string
  currentSnapshot: string
  restoreM2MContent: string
  afterHookAfterRestore: string
  ctxCamelSingular: string
  ctxSingularText: string
}): string {
  return `'use server'

import db from '@admin/db'
import { requireRole, UserRole } from '@admin/auth/middleware'
import { ${join} } from '@admin/db/schema'
import { and, eq } from 'drizzle-orm'
import { ${ctxCacheInvalidationFn} } from 'next/cache'
import { createEntityVersion, entityVersionsCacheTags } from '@admin/actions/entity-versions'
import { ${ctxCamelPlural}CacheTags } from './types'
import { get${ctxSingular}ById } from './get-${kebabSingular}-by-id'
import {
  build${ctxSingular}RestoreData,
  ${snapshotImport}
} from './helpers'
${lifecycleHookImport}
import type { ${ctxSingular}UpdateResult, ${ctxSingular} } from './types'

export async function restore${ctxSingular}Version(
  versionId: string
): Promise<${ctxSingular}UpdateResult> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])

  try {
    const actorId = actor.id
    const now = new Date().toISOString()

    const [versionRow] = await db
      .select()
      .from(entityVersions)
      .where(and(eq(entityVersions.id, versionId), eq(entityVersions.entityType, '${ctxPlural}')))
      .limit(1)

    if (!versionRow) throw new Error('${ctxSingular} version not found')

    const versionData = versionRow.data as Record<string, unknown>
    const restoreData = build${ctxSingular}RestoreData(versionData)
    const auditRequestMetadata = await getAuditRequestMetadata()
    const lifecycleContext = ${restoreContext}
    ${beforeHook}

    const result = await db.transaction(async (tx) => {
      const [currentRow] = await tx
        .select()
        .from(${ctxTableVar})
        .where(eq(${ctxTableVar}.id, versionRow.entityId))
        .limit(1)

      if (!currentRow) throw new Error('${ctxSingular} not found')

      await createEntityVersion(tx, {
        entityType: '${ctxPlural}',
        entityId: versionRow.entityId,
        data: ${currentSnapshot},
        createdBy: actorId
      })
${restoreM2MContent}
      return tx.update(${ctxTableVar})
        .set({ ...restoreData, updatedAt: now, updatedBy: actorId })
        .where(eq(${ctxTableVar}.id, versionRow.entityId))
        .returning()
    })

    if (result.length === 0) throw new Error('${ctxSingular} not found')

    ${ctxCacheInvalidationFn}(${ctxCamelPlural}CacheTags.all)
    ${ctxCacheInvalidationFn}(${ctxCamelPlural}CacheTags.byId(versionRow.entityId))
    ${ctxCacheInvalidationFn}(entityVersionsCacheTags.for('${ctxPlural}', versionRow.entityId))
    const restored${ctxSingular} = await get${ctxSingular}ById(versionRow.entityId)
    ${afterHookAfterRestore}

    return {
      success: true,
      ${ctxCamelSingular}: restored${ctxSingular} ?? (result[0] as unknown as ${ctxSingular})
    }
  } catch (error) {
    console.error('Error restoring ${ctxSingularText} version:', error)
    throw new Error(error instanceof Error ? error.message : 'Failed to restore ${ctxSingularText} version')
  }
}
`
}

function renderGenRestoreContentTemplate({ ctxSingular }: { ctxSingular: string }): string {
  return `{
      ...lifecycleContext,
      data: (restored${ctxSingular} ?? result[0]) as unknown as Record<string, unknown>
    }`
}

export function renderGenRestoreContentRenderer(
  request:
    | {
        kind: 'renderGenRestoreContentServerActionTemplate'
        props: {
          join: string
          ctxCacheInvalidationFn: string
          ctxCamelPlural: string
          ctxSingular: string
          kebabSingular: string
          snapshotImport: string
          lifecycleHookImport: string
          ctxPlural: string
          restoreContext: string
          beforeHook: string
          ctxTableVar: string
          currentSnapshot: string
          restoreM2MContent: string
          afterHookAfterRestore: string
          ctxCamelSingular: string
          ctxSingularText: string
        }
      }
    | { kind: 'renderGenRestoreContentTemplate'; props: { ctxSingular: string } }
): string {
  switch (request.kind) {
    case 'renderGenRestoreContentServerActionTemplate':
      return renderGenRestoreContentServerActionTemplate(request.props)
    case 'renderGenRestoreContentTemplate':
      return renderGenRestoreContentTemplate(request.props)
  }

  throw new Error('Unsupported renderer request')
}
